import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../Styles/home.css';

const heroBackgrounds = [
  '/assets/Home_bg_img_1.png',
  '/assets/Home_bg_img_2.JPG',
  '/assets/Home_bg_img_3.png',
];

const hrCards = [
  {
    title: 'HR Leadership Development',
    content:
      'Equip HR professionals and business leaders with future-ready leadership skills through summits, workshops, and executive learning experiences.',
  },
  {
    title: 'Workplace Culture & Employer Branding',
    content:
      'Build high-performing workplaces by strengthening employer reputation, employee engagement, and organizational culture.',
  },
  {
    title: 'Talent & Workforce Transformation',
    content:
      'Enable organizations to attract, develop, and retain exceptional talent while preparing for the future of work.',
  },
  {
    title: 'HR Innovation & Digital Transformation',
    content:
      'Drive business success by embracing HR technology, data-driven strategies, and innovative people practices that transform the employee experience.',
  },
];

// Each feature card: icon image, accent color (matches the colored square
// behind the icon in Figma), title, and its own 3-item checklist.
const featureCards = [
  {
    icon: '/assets/Home_grid_icon_1.png',
    color: 'home-color-1',
    title: 'Curated Community Members',
    checklist: [
      'Verified Industry Professionals',
      'Exclusive HR Community',
      'Curated for Meaningful Networking',
    ],
  },
  {
    icon: '/assets/Home_grid_icon_2.png',
    color: 'home-color-2',
    title: 'Insightful, Real-World Discussions',
    checklist: [
      'Expert-Led Insights',
      'Real-World HR Perspectives',
      'Actionable Industry Discussions',
    ],
  },
  {
    icon: '/assets/Home_grid_icon_3.png',
    color: 'home-color-3',
    title: 'Focus on Talent, People Success & Branding',
    checklist: [
      'Develop Talent',
      'Drive People Success',
      'Build Strong Brands',
    ],
  },
  {
    icon: '/assets/Home_grid_icon_4.png',
    color: 'home-color-4',
    title: 'Opportunities to Collaborate & Grow',
    checklist: [
      'Strategic Partnerships',
      'Meaningful Networking',
      'Business Growth Opportunities',
    ],
  },
  {
    icon: '/assets/Home_grid_icon_5.png',
    color: 'home-color-5',
    title: 'A Platform to Learn, Lead, and Influence',
    checklist: [
      'Learn through expert insights',
      'Lead with confidence',
      'Influence the future of HR',
    ],
  },
  {
    icon: '/assets/Home_grid_icon_6.png',
    color: 'home-color-6',
    title: 'Execution Mindset - Turning Ideas into Action',
    checklist: [
      'Plan with Purpose',
      'Act with Discipline',
      'Deliver Measurable Results',
    ],
  },
];

const statsData = [
  { icon: '/assets/Home_icon_1.png', target: 10000, suffix: '+', label: 'Community Members' },
  { icon: '/assets/Home_icon_2.png', target: 200, suffix: '+', label: 'Overall Engagements' },
  { icon: '/assets/Home_icon_3.png', target: 100, suffix: '+', label: 'Partners' },
  { icon: '/assets/Home_icon_4.png', target: 50, suffix: '+', label: 'Events Hosted' },
  { icon: '/assets/Home_icon_5.png', target: 20, suffix: '+', label: 'Industries Covered' },
  { icon: '/assets/Home_icon_6.png', target: 90, suffix: '%', label: 'Community Growth Rate' },
];

function Home() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [openCard, setOpenCard] = useState(-1); // all cards collapsed by default — only open on click

  // ----- About illustration: "rise up" entrance when it scrolls into view -----
  const [aboutVisible, setAboutVisible] = useState(false);
  const aboutImgRef = useRef(null);

  useEffect(() => {
    const el = aboutImgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAboutVisible(true);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ----- Stats: count up from 0 to each target once the section is in view -----
  const statsSectionRef = useRef(null);
  const statsAnimated = useRef(false);
  const [statCounts, setStatCounts] = useState(statsData.map(() => 0));

  useEffect(() => {
    const el = statsSectionRef.current;
    if (!el) return;

    const animateCounts = () => {
      const duration = 1600;
      const startTime = performance.now();
      const targets = statsData.map((s) => s.target);

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setStatCounts(targets.map((t) => Math.round(t * eased)));
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !statsAnimated.current) {
            statsAnimated.current = true;
            animateCounts();
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroBackgrounds.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const toggleCard = (index) => {
    setOpenCard(openCard === index ? -1 : index);
  };

  return (
    <>
      <section className="home-hero">
  {/* Full-screen looping background */}
  <div className="home-hero-bg-slideshow">
    {heroBackgrounds.map((src, index) => (
      <img
        key={src}
        src={src}
        alt="Vanakkam HRD community"
        className={`home-hero-bg-slide ${index === activeSlide ? 'home-active' : ''}`}
      />
    ))}
  </div>

  {/* Dark left-to-right shade for text readability */}
  <div className="home-hero-overlay"></div>

  {/* Actual content, sits above the background */}
  <div className="home-hero-content">
    <div className="home-hero-left">
      <h1 className="home-hero-title">
        <span className="home-text-engage">ENGAGE.</span>
        <span className="home-text-inspire">INSPIRE.</span>
        <span className="home-text-transform">TRANSFORM.</span>
      </h1>

      <p className="home-hero-subtitle">
        Elevate Human Potential, Community - Driven People Success.
      </p>

      <Link to="/jointhecommunity" className="home-hero-btn">
        Join the Community
        <img
          src="/assets/home_arrow_icon.png"
          alt="Arrow Icon"
          className="home-arrow-icon"
        />
      </Link>
    </div>
  </div>
</section>

      {/* ===== SECTION 2: About / HR Stars ===== */}
      <section className="home-about-section">
        <div className="home-about-left">
        <img
          ref={aboutImgRef}
          src="/assets/home_02.png"
          alt="HR professionals workspace illustration"
          className={`home-about-illustration ${aboutVisible ? 'home-in-view' : ''}`}
        />
      </div>

        <div className="home-about-right">
          <h2 className="home-about-heading">
            We Are? <span className="home-about-highlight">HR STARS!</span>
          </h2>

          <p className="home-about-text">
            Vanakkam HRD is a thriving community of forward-thinking HR professionals and business leaders shaping the future of work. We bring together talent experts and people leaders to share insights, discuss challenges, and build impactful solutions - driving HR innovation from local to global.
          </p>

          <p className="home-about-text">
           Join a highly curated ecosystem of People Success and HR leaders across India. Connect, collaborate, and grow through powerful conversations, shared knowledge, and real - world solutions.
          </p>
        </div>
      </section>

      {/* ===== SECTION 3: Stats ===== */}
      <section className="home-stats-section" ref={statsSectionRef}>
        {statsData.map((stat, index) => (
          <div className="home-stat-item" key={stat.label}>
            <div className="home-stat-circle">
              <img src={stat.icon} alt="Icon" className="home-stat-icon" />
            </div>
            <h3 className="home-stat-number">
              {statCounts[index].toLocaleString()}{stat.suffix}
            </h3>
            <p className="home-stat-label">{stat.label}</p>
          </div>
        ))}
      </section>

      {/* ===== SECTION 4: Transformation Through HR ===== */}
      <section className="home-transformation-section">
        <h2 className="home-transformation-heading">
          <span className="home-transformation-highlight">Transformation</span> Through HR
        </h2>

        <div className="home-transformation-content">
          <div className="home-transformation-left">
            <img
              src="/assets/home_01.png"
              alt="HR transformation illustration"
              className="home-transformation-image"
            />
          </div>

          <div className="home-transformation-right">
            {hrCards.map((card, index) => (
              <div className="home-hr-card" key={card.title}>
                <button
                  type="button"
                  className="home-hr-card-header"
                  onClick={() => toggleCard(index)}
                >
                  <span className="home-hr-card-dot"></span>
                  <span className="home-hr-card-title">{card.title}</span>
                  <span className={`home-hr-card-arrow ${openCard === index ? 'home-open' : ''}`}>
                  <img 
                    src="/assets/hone_dd_arrow.png" 
                    alt="Dropdown Arrow" 
                    className="home-hr-card-arrow-img"
                  />
                </span>
                </button>

                {openCard === index && (
                  <div className="home-hr-card-body">
                    <p>{card.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: Upcoming Events ===== */}
      <section className="home-events-section">
        <h2 className="home-events-heading">Upcoming Events</h2>
        <div className="home-events-line"></div>

        <div className="home-events-card">
          <img
            src="/assets/upe_home_img.png"
            alt="TN Annual HR Summit 2026"
            className="home-events-image"
          />

          <h3 className="home-events-title">TN Annual HR Summit 2026</h3>

          <div className="home-events-info">
            {/* Calendar icon */}
            <svg
              className="home-events-info-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <line x1="8" y1="3" x2="8" y2="7" />
              <line x1="16" y1="3" x2="16" y2="7" />
            </svg>
            <span>19th December, 2026 ( Saturday )</span>
          </div>

          <div className="home-events-info">
            {/* Location pin icon */}
            <svg
              className="home-events-info-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
              <circle cx="12" cy="9.5" r="2.5" />
            </svg>
            <span>Radisson Blu Hotel &amp; Suites GRT, Chennai</span>
          </div>

          <Link to="/events" className="home-events-btn">
            View Details
            <img
              src="/assets/arrow _icon_get.png"
              alt="Arrow Icon"
              className="home-events-btn-arrow"
            />
          </Link>
        </div>
      </section>

      {/* ===== SECTION 6: What Sets Us Apart ===== */}
      <section className="home-features-section">
        <h2 className="home-features-heading">What Sets Us Apart</h2>
        <p className="home-features-subtext">
          At Vanakkam HRD, we believe community should create impact not just conversations.
        </p>

        <div className="home-features-grid">
          {featureCards.map((card) => (
            <div className="home-feature-card" key={card.title}>
              <div className="home-feature-header">
                <div className={`home-feature-icon ${card.color}`}>
                  <img
                    src={card.icon}
                    alt="Feature Icon"
                    className="home-feature-icon-img"
                  />
                </div>
                <p className="home-feature-title">{card.title}</p>
              </div>

              <ul className="home-feature-checklist">
                {card.checklist.map((item) => (
                  <li className="home-check-item" key={item}>
                    <img
                      src="/assets/check _icon.png"
                      alt="Check"
                      className="home-check-icon"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export default Home;